// Active Builds: client builds and their pages/screens (migration
// 20261103140000_active_builds.sql). A page is backlog -> queued (released onto
// the Fulfillment board as a client_task) -> done (first draft shipped). The
// daily scheduler releases the top backlog page on each day the build's cadence
// lands on; "Release next" does the same by hand.

import { supabase } from './supabase';

export type BuildFreq = 'daily' | 'weekly' | 'monthly';
export type BuildStatus = 'active' | 'paused' | 'finished';
export type PageStatus = 'backlog' | 'queued' | 'done';
export type PageKind = 'page' | 'screen';

export interface ActiveBuild {
  id: string;
  client_id: string;
  name: string;
  notes: string | null;
  status: BuildStatus;
  assigned_to: string | null;
  freq: BuildFreq;
  interval_n: number;
  byweekday: number[] | null;
  bymonthday: number | null;
  start_date: string;
  hold_while_open: boolean;
  auto_release: boolean;
  released_through: string;
  seed_source: string | null;
  seed_evidence: string | null;
  created_at: string;
  client?: { name: string } | null;
}

export interface BuildPage {
  id: string;
  build_id: string;
  title: string;
  path: string | null;
  kind: PageKind;
  status: PageStatus;
  position: number;
  note: string | null;
  task_id: string | null;
  released_at: string | null;
  done_at: string | null;
  /** The Trello card the page came from, if any (20261104180000_build_page_trello_card.sql). */
  card_id: string | null;
  card_name: string | null;
  card_url: string | null;
  card_board_id: string | null;
  task?: { id: string; status: string | null; due_date: string | null } | null;
}

/** What a page needs to know about its Trello card. boardId is the client's comms_boards row. */
export interface PageCard {
  id: string;
  name: string;
  url: string;
  listName: string | null;
  boardId: string;
}

export const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export function cadenceLabel(b: Pick<ActiveBuild, 'freq' | 'interval_n' | 'byweekday' | 'bymonthday' | 'start_date'>): string {
  const n = b.interval_n;
  if (b.freq === 'daily') return n > 1 ? `Every ${n} days` : 'Every day';
  if (b.freq === 'weekly') {
    const days = (b.byweekday ?? []).slice().sort((a, c) => a - c).map((d) => DAY_LABELS[d]).join(', ');
    const base = n > 1 ? `Every ${n} weeks` : 'Weekly';
    return days ? `${base} on ${days}` : base;
  }
  const dom = b.bymonthday ?? Number(b.start_date.slice(8, 10));
  return `${n > 1 ? `Every ${n} months` : 'Monthly'} on day ${dom}`;
}

/** Pages per week the cadence releases, for the "backlog clears in ~N weeks" hint. */
export function pagesPerWeek(b: Pick<ActiveBuild, 'freq' | 'interval_n' | 'byweekday'>): number {
  if (b.freq === 'daily') return 7 / b.interval_n;
  if (b.freq === 'weekly') return Math.max(1, b.byweekday?.length ?? 1) / b.interval_n;
  return 12 / 52 / b.interval_n;
}

export async function fetchBuilds(): Promise<ActiveBuild[]> {
  const { data, error } = await supabase
    .from('active_builds')
    .select('*, client:clients(name)')
    .order('created_at');
  if (error) throw error;
  return (data as unknown as ActiveBuild[]) ?? [];
}

export async function fetchPages(buildId: string): Promise<BuildPage[]> {
  const { data, error } = await supabase
    .from('active_build_pages')
    .select('*, task:client_tasks(id, status, due_date)')
    .eq('build_id', buildId)
    .order('position')
    .order('created_at');
  if (error) throw error;
  return (data as unknown as BuildPage[]) ?? [];
}

export async function saveBuild(id: string | null, patch: Partial<ActiveBuild>): Promise<string> {
  const { client: _client, ...row } = patch;
  void _client;
  if (id) {
    const { error } = await supabase.from('active_builds').update(row).eq('id', id);
    if (error) throw error;
    return id;
  }
  if (!row.client_id) throw new Error('A build needs a client');
  const { data, error } = await supabase
    .from('active_builds')
    .insert({ ...row, client_id: row.client_id })
    .select('id')
    .single();
  if (error) throw error;
  return (data as { id: string }).id;
}

export async function deleteBuild(id: string): Promise<void> {
  const { error } = await supabase.from('active_builds').delete().eq('id', id);
  if (error) throw error;
}

export async function addPage(buildId: string, title: string, path: string | null, kind: PageKind, position: number): Promise<void> {
  const { error } = await supabase
    .from('active_build_pages')
    .insert({ build_id: buildId, title, path, kind, position, status: 'backlog' });
  if (error) throw error;
}

export async function updatePage(id: string, patch: Partial<Pick<BuildPage, 'title' | 'path' | 'kind' | 'status' | 'note' | 'position'>>): Promise<void> {
  const { error } = await supabase.from('active_build_pages').update(patch).eq('id', id);
  if (error) throw error;
}

/**
 * Attach a Trello card to a page, or detach it (null).
 *
 * A backlog page only stores the card: release_build_page_as() links it to the
 * task when the page goes to the board. A page already on the board has its
 * task, so the card is linked now, the same upsert on card_id Client Comms uses
 * (a card belongs to one task at a time), plus the manual_import row the Board
 * Explorer writes when the card has no comms rows yet, so the chip shows.
 *
 * Detaching only clears the page. A chip already on a task stays there; remove
 * it from the task like any other chip.
 */
export async function setPageCard(page: BuildPage, card: PageCard | null, actorName: string): Promise<void> {
  const { data, error } = await supabase
    .from('active_build_pages')
    .update({
      card_id: card?.id ?? null,
      card_name: card?.name ?? null,
      card_url: card?.url ?? null,
      card_board_id: card?.boardId ?? null,
    })
    .eq('id', page.id)
    .select('id');
  if (error) throw error;
  // Read back: an RLS-blocked update reports no error, only no row.
  if (!data?.length) throw new Error('The page wasn’t updated.');
  if (!card || page.status !== 'queued' || !page.task_id) return;

  const { error: importError } = await supabase.from('comms_items').upsert(
    {
      trello_action_id: `manual-${card.id}`,
      board_id: card.boardId,
      card_id: card.id,
      card_name: card.name,
      card_url: card.url,
      card_list_name: card.listName,
      kind: 'manual_import',
      actor_name: actorName,
      actor_trello_id: 'manual',
      body: null,
      occurred_at: new Date().toISOString(),
      status: 'open',
      direction: 'inbound',
    },
    { onConflict: 'trello_action_id', ignoreDuplicates: true },
  );
  if (importError) throw importError;

  const { error: linkError } = await supabase
    .from('comms_task_links')
    .upsert({ card_id: card.id, task_id: page.task_id, completed_at: null }, { onConflict: 'card_id' });
  if (linkError) throw linkError;
}

/** The client's active comms board, where its page cards come from. */
export async function fetchClientBoard(clientId: string): Promise<{ id: string; trello_board_id: string; board_name: string } | null> {
  const { data, error } = await supabase
    .from('comms_boards')
    .select('id, trello_board_id, board_name')
    .eq('client_id', clientId)
    .eq('is_active', true)
    .limit(1)
    .maybeSingle();
  if (error) throw error;
  return data ?? null;
}

export async function deletePage(id: string): Promise<void> {
  const { error } = await supabase.from('active_build_pages').delete().eq('id', id);
  if (error) throw error;
}

/** Rewrite positions to match the given order (backlog reorder). */
export async function reorderPages(ids: string[]): Promise<void> {
  const results = await Promise.all(
    ids.map((id, i) => supabase.from('active_build_pages').update({ position: i }).eq('id', id)),
  );
  const failed = results.find((r) => r.error);
  if (failed?.error) throw failed.error;
}

/** Release the top backlog page onto the Fulfillment board now. Returns the task id, or null when the backlog is empty. */
export async function releaseNext(buildId: string): Promise<string | null> {
  const { data, error } = await supabase.rpc('release_build_page', { p_build_id: buildId });
  if (error) throw error;
  return (data as string | null) ?? null;
}

/** done / queued / backlog per build, for the rail. */
export async function fetchPageCounts(): Promise<Record<string, { done: number; queued: number; backlog: number }>> {
  const { data, error } = await supabase.from('active_build_pages').select('build_id, status').range(0, 9999);
  if (error) throw error;
  const out: Record<string, { done: number; queued: number; backlog: number }> = {};
  for (const r of (data ?? []) as Array<{ build_id: string; status: PageStatus }>) {
    const c = (out[r.build_id] ??= { done: 0, queued: 0, backlog: 0 });
    c[r.status] += 1;
  }
  return out;
}
